import type { CellObject, ColInfo } from "xlsx";
import {
  DEFAULT_COL_INFO,
  makeFormatWorksheetColumnWithXlsx,
  XlsxExportColumnFormatParams,
} from "utils/xlsxUtils";
import type { ColumnDefinition } from "utils/xlsxImportExport/XlsxImportColumn";
import {
  ExtractValidateRowType,
  SheetKeysForWorkSheetDefs,
  ValidatedData as ValidatedDataBySheetKey,
} from "./XlsxImportWorkBook";
import {
  ValidatedData,
  ValidatedRow,
  WorkSheetDefinition,
} from "./XlsxImportWorkSheet";

export type ColumnFormatsByKey<TValidatedData extends ValidatedData> = {
  [K in keyof TValidatedData]?: XlsxExportColumnFormatParams<
    TValidatedData extends ValidatedRow ? TValidatedData : never
  >;
};

export function getWorkSheetExportData<
  TWorkSheetDef extends WorkSheetDefinition,
  TValidatedData extends ExtractValidateRowType<
    TWorkSheetDef["key"],
    TWorkSheetDef
  >,
>(
  workSheetDefinition: TWorkSheetDef,
  data: TValidatedData[],
  formatsByKey?: Partial<ColumnFormatsByKey<TValidatedData>>,
) {
  const header = workSheetDefinition.columnDefinitions.map(
    (colDef) => colDef.header || String(colDef.key),
  );

  const rows = data.map((rowData) =>
    workSheetDefinition.columnDefinitions.reduce<
      TValidatedData[keyof TValidatedData][]
    >((row, colDef) => {
      const parseValue =
        formatsByKey?.[colDef.key as keyof TValidatedData]?.parseValue;
      row.push(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        parseValue ? parseValue(rowData[colDef.key]) : rowData[colDef.key],
      );
      return row;
    }, []),
  );
  return [header, ...rows];
}

export type ColumnFormatsByColumnBySheet<
  TWorkSheetDef extends WorkSheetDefinition,
> = {
  [P in SheetKeysForWorkSheetDefs<TWorkSheetDef>]?: ColumnFormatsByKey<
    ExtractValidateRowType<P, TWorkSheetDef>
  >;
};

type ColumnFormat = { columnIndex: number; format: string };

type ExportWorkSheetResult<TWorkSheetDef extends WorkSheetDefinition> = {
  columnFormats: ColumnFormat[];
  columnInfo: ColInfo[];
  data: unknown[][];
  processCells: (getCell: (row: number, column: number) => CellObject) => void;
  sheetName: string;
  workSheetDefinition: TWorkSheetDef;
};

export function getWorkBookExportData<
  TWorkSheetDefs extends WorkSheetDefinition,
>(
  workSheetDefinitions: TWorkSheetDefs[],
  dataBySheetKey: ValidatedDataBySheetKey<TWorkSheetDefs>,
  columnFormatsBySheetKey?: ColumnFormatsByColumnBySheet<TWorkSheetDefs>,
) {
  return workSheetDefinitions.map<ExportWorkSheetResult<TWorkSheetDefs>>(
    (workSheetDefinition) => {
      const sheetKey =
        workSheetDefinition.key as SheetKeysForWorkSheetDefs<TWorkSheetDefs>;

      const columnFormatParams = columnFormatsBySheetKey?.[sheetKey];

      const data = getWorkSheetExportData(
        workSheetDefinition,
        dataBySheetKey[sheetKey],
        columnFormatParams,
      );

      const columnFormats: ColumnFormat[] = [];

      const cellProcessingColumnIndices: number[] = [];

      const columnInfo = workSheetDefinition.columnDefinitions.map<ColInfo>(
        (colDef, columnIndex) => {
          const params = columnFormatParams?.[colDef.key as string] || {};

          if (params.columnFormat) {
            columnFormats.push({ columnIndex, format: params.columnFormat });
          }

          if (params.processCell) {
            cellProcessingColumnIndices.push(columnIndex);
          }

          return params.columnInfo || DEFAULT_COL_INFO;
        },
      );

      const processCells = (
        getCell: (row: number, column: number) => CellObject,
      ) => {
        if (cellProcessingColumnIndices.length === 0) return;

        dataBySheetKey[sheetKey].forEach((row, rowIndex) => {
          cellProcessingColumnIndices.forEach((columnIndex) => {
            const cellObj = getCell(rowIndex + 1, columnIndex);
            const columnDef = workSheetDefinition.columnDefinitions[
              columnIndex
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ] as ColumnDefinition<any, any>;
            const columnFormat = columnFormatParams?.[columnDef.key as string];
            columnFormat?.processCell?.(cellObj, { columnDef, row });
          });
        });
      };

      return {
        columnFormats,
        columnInfo,
        data,
        processCells,
        sheetName:
          workSheetDefinition.sheetName || String(workSheetDefinition.key),
        workSheetDefinition,
      };
    },
  );
}

export async function getWorkBookExport<
  TWorkSheetDefs extends WorkSheetDefinition,
>(
  workSheetDefinitions: TWorkSheetDefs[],
  dataBySheetKey: ValidatedDataBySheetKey<TWorkSheetDefs>,
  columnFormatsBySheetKey?: ColumnFormatsByColumnBySheet<TWorkSheetDefs>,
) {
  const workSheetsData = getWorkBookExportData(
    workSheetDefinitions,
    dataBySheetKey,
    columnFormatsBySheetKey,
  );

  const { default: xlsx } = await import("xlsx");

  const formatColumn = makeFormatWorksheetColumnWithXlsx(xlsx.utils);

  const workBook = xlsx.utils.book_new();

  workSheetsData.forEach(
    ({ columnFormats, columnInfo, data, processCells, sheetName }) => {
      const workSheet = xlsx.utils.aoa_to_sheet(data);

      columnFormats.forEach(({ columnIndex, format }) =>
        formatColumn(workSheet, columnIndex, format),
      );

      workSheet["!cols"] = columnInfo;

      processCells((row: number, column: number) => {
        const ref = xlsx.utils.encode_cell({ r: row, c: column });
        return workSheet[ref] as CellObject;
      });

      xlsx.utils.book_append_sheet(workBook, workSheet, sheetName);
    },
  );

  return workBook;
}
